import { Component, DestroyRef, EventEmitter, Inject, inject, Input, OnInit, Optional, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { SolPropertiesEstimateSettingsComponent } from './sol-properties-estimate-settings/sol-properties-estimate-settings.component';
import { SolPropertiesEstimateInstallTypeComponent } from './sol-properties-estimate-install-type/sol-properties-estimate-install-type.component';
import { SolPropertiesEstimateCollaborationComponent } from './sol-properties-estimate-collaboration/sol-properties-estimate-collaboration.component';
import { SolPropertiesEstimateStatisticsComponent } from './sol-properties-estimate-statistics/sol-properties-estimate-statistics.component';
import { CONSTANTS } from '../../../../Constants/Constants';
import { SolPropertiesElectricalServicesComponent } from './sol-properties-electrical-services/sol-properties-electrical-services.component';
import { SolPropertiesSizingSettingsComponent } from './sol-properties-sizing-settings/sol-properties-sizing-settings.component';
import { SolPropertiesTravelSettingsComponent } from './sol-properties-travel-settings/sol-properties-travel-settings.component';
import { SolpropertiesTaxSettingsComponent } from './sol-properties-tax-settings/sol-properties-tax-settings.component';
import { SolPropertiesOvertimeRatesComponent } from './sol-properties-overtime-rates/sol-properties-overtime-rates.component';
import { SolPropertiesProposalCustomerInfoComponent } from './sol-properties-proposal-customer-info/sol-properties-proposal-customer-info.component';
import { SolPropertiesProposalComponent } from './sol-properties-proposal/sol-properties-proposal.component';
import { SolPropertiesLaborRatesComponent } from './sol-properties-labor-rates/sol-properties-labor-rates.component';
import { SolPropertiesTaskLaborBasedComponent } from './sol-properties-task-based-labor/sol-properties-task-based-labor.component';
import { PropertyOption } from '../../../../models/properties/PropertyOptions';
import { SolPropertiesService } from '../../../../services/properties/sol-properties.service';
import { PropertyOptionId, CULTURE_ID } from '../../../../models/enum';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonDataService } from '../../../../services/common-data.service';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { CollaborationState } from '../../../../models/dtos/Enums/CollaborationState';
import { CollaborationApiService } from '../../../../services/api/collaboration-api.service';
import { GocCollaborationRestrictionHandlerService } from '../../../../services/common/goc-collaboration-restriction-handler.service';
import { CommonFunctionsService } from '../../../../services/common-functions.service';
import { HasUnsavedChanges, registerUnsavedChanges } from '../../../../services/common/unsaved-changes.service';

@Component({
  selector: 'app-sol-properties',
  standalone: true,
  imports: [
    CommonModule,
    MatListModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDividerModule,
    ReactiveFormsModule,
    TranslatePipe,
    SolPropertiesEstimateSettingsComponent,
    SolPropertiesEstimateInstallTypeComponent,
    SolPropertiesEstimateCollaborationComponent,
    SolPropertiesEstimateStatisticsComponent,
    SolPropertiesElectricalServicesComponent,
    SolPropertiesSizingSettingsComponent,
    SolPropertiesTravelSettingsComponent,
    SolpropertiesTaxSettingsComponent,
    SolPropertiesOvertimeRatesComponent,
    SolPropertiesProposalCustomerInfoComponent,
    SolPropertiesProposalComponent,
    SolPropertiesLaborRatesComponent,
    SolPropertiesTaskLaborBasedComponent
  ],
  templateUrl: './sol-properties.component.html',
  styleUrl: './sol-properties.component.scss',
  providers: [SolPropertiesService]
})
export class SolPropertiesComponent implements OnInit, HasUnsavedChanges {
  @Input() initialSelectedTab: string = CONSTANTS.EMPTY_STRING;
  @Output() cancel = new EventEmitter<void>();
  selectedOptionId: string = CONSTANTS.EMPTY_STRING;
  propertiesOptionId = PropertyOptionId;
  isReadOnly: boolean = false;
  private destroyRef = inject(DestroyRef);

  propertyOptions: PropertyOption[] = [
    {
      id: PropertyOptionId.ESTIMATE_SETTINGS,
      label: 'ESTIMATE_SETTINGS',
      isVisible: true
    },
    {
      id: PropertyOptionId.ESTIMATE_COLLABORATION,
      label: 'ESTIMATE_COLLABORATION',
      isVisible: true
    },
    {
      id: PropertyOptionId.ESTIMATE_INSTALL_TYPE,
      label: 'ESTIMATE_INSTALL_TYPE',
      isVisible: true
    },
    {
      id: PropertyOptionId.ESTIMATE_STATISTICS,
      label: 'ESTIMATE_STATISTICS',
      isVisible: true
    },
    {
      id: PropertyOptionId.ELECTRICAL_SERVICES,
      label: 'ELECTRICAL_SERVICES',
      isVisible: false
    },
    {
      id: PropertyOptionId.SIZING_SETTINGS,
      label: 'SIZING_SETTINGS',
      isVisible: false
    },
    {
      id: PropertyOptionId.TASK_BASED_LABOR,
      label: 'TASK_BASED_LABOR',
      isVisible: false
    },
    {
      id: PropertyOptionId.TRAVEL_SETTINGS,
      label: 'TRAVEL_SETTINGS',
      isVisible: false
    },
    {
      id: PropertyOptionId.TAX_SETTINGS,
      label: 'TAX_SETTINGS',
      isVisible: true
    },
    {
      id: PropertyOptionId.LABOR_RATES,
      label: 'LABOR_RATES',
      isVisible: true
    },
    {
      id: PropertyOptionId.OVERTIME_RATES,
      label: 'OVERTIME_RATES',
      isVisible: true
    },
    {
      id: PropertyOptionId.PROPOSAL,
      label: 'PROPOSAL',
      isVisible: true
    },
    {
      id: PropertyOptionId.PROPOSAL_CUSTOMER_INFO,
      label: 'PROPOSAL_CUSTOMER_INFO',
      isVisible: true
    }
  ];

  trackById(index: number, option: PropertyOption): string {
    return option.id;
  }

  constructor(
    private readonly solPropertiesService: SolPropertiesService,
    private readonly commonDataService: CommonDataService,
    private readonly collaborationApiService: CollaborationApiService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly userProfileService: UserProfileService,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly dialogData: { selectedTab?: string }
  ) {
    registerUnsavedChanges(this);
  }

  ngOnInit(): void {
    const initialTab = this.initialSelectedTab || this.dialogData?.selectedTab;
    if (initialTab && this.propertyOptions.some(o => o.id === initialTab)) {
      this.selectedOptionId = initialTab;
    } else {
      this.selectedOptionId = this.propertyOptions[0].id;
    }
    this.solPropertiesService.callEstimateApis();
    this.setupReadOnlyMode();
  }

  hasUnsavedChanges(): boolean {
    // Submitted / read-only estimates never prompt to save.
    return !this.commonDataService.isReadOnlyMode() && this.solPropertiesService.hasPropertiesChanged();
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.readOnlyMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isReadOnly => {
        this.isReadOnly = isReadOnly;
      });
  }

  isDataLoaded(): boolean {
    return !this.solPropertiesService.isLoading;
  }

  selectOption(optionId: string): void {
    this.selectedOptionId = optionId;
  }

  getSelectedOption(): PropertyOption | undefined {
    return this.propertyOptions.find(option => option.id === this.selectedOptionId);
  }

  trackByOptionId(index: number, option: PropertyOption): string {
    return option.id;
  }

  /**
   * Determines if a tab should be visible based on both the default isVisible flag
   * and culture-specific rules (e.g., TAX_SETTINGS is hidden for Canada)
   */
  isTabVisible(optionId: string): boolean {
    // First check if tab is marked as visible by default
    const option = this.propertyOptions.find(o => o.id === optionId);
    if (!option || !option.isVisible) {
      return false;
    }

    // Hide TAX_SETTINGS tab for Canada cultures
    if (optionId === PropertyOptionId.TAX_SETTINGS) {
      const cultureId = this.commonDataService.getEstimateCultureId();
      return cultureId !== CULTURE_ID.FR_CA && cultureId !== CULTURE_ID.EN_CA;
    }

    return true;
  }

  /** @returns true when the changes were saved and the panel closed; false when the user must stay. */
  async save(): Promise<boolean> {

    const currentState = this.solPropertiesService.getCollaborationData().collaborationState;
    const isCollaborationEnabled = currentState !== CollaborationState.NonCollaborative;

    if (currentState === CollaborationState.Locked) {
      return false;
    }

    if (this.isReadOnly) {
      if (this.solPropertiesService.hasPropertiesChanged()) {
        await this.solPropertiesService.savePropertiesToCommonDataService();
        this.commonDataService.isSaveEnabled = true;
      }
      this.cancelSelection();
      return true;
    }

    // first check 
    // GOC restriction before saving any changes
    await this.checkGOCRestriction();

    // Changing the project duration can invalidate escalation; the user must accept the reset.
    if (!await this.solPropertiesService.confirmEscalationResetForProjectDateChange()) {
      return false;
    }

    if (this.solPropertiesService.hasNonCollaborationChanges()) {
      this.commonDataService.isSaveEnabled = true;
    }

    const wasInitiallyCollaborative =
      this.solPropertiesService.getInitialCollaborationState() !== CollaborationState.NonCollaborative;

    if (isCollaborationEnabled && !wasInitiallyCollaborative) {
      const currentCollaborators = this.solPropertiesService.getCollaboratorList();
      const otherCollaborators = currentCollaborators.filter(collaborator => collaborator.email !== this.userProfileService.userInfo?.mail);
      if (otherCollaborators.length === 0) {
        this.solPropertiesService.notifyNoCollaborators();
        return false;
      }
    }

    await this.solPropertiesService.savePropertiesToCommonDataService();

    const result = await this.solPropertiesService.applyCollaborationChanges();
    if (result) {
      this.cancelSelection();
    }
    return result;
  }

  private async checkGOCRestriction(): Promise<void> {
    if (this.commonFunctionsService.isUserGOC()) {
      try {
        await firstValueFrom(this.collaborationApiService.getIsGOCRestricted());
      } catch (error) {
        if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
          return;
        }

        throw error;
      }
    }
  }

  cancelSelection(): void {
    this.cancel.emit();
  }
}
